import { useCallback, useState } from 'react';

import { JobSummary } from 'domain/entities/JobSummary';
import { isEntityLocked } from 'domain/entities/BillingStatus';
import { AttachmentFile } from 'domain/entities/Attachment';
import { useCaseUpdateJob } from 'application/jobs/useCases/useCaseUpdateJob';
import usePermission from 'application/auth/hooks/usePermission';
import { JobResourceType } from 'application/auth/utils/resources';
import { useCaseCompleteJob } from 'application/jobs/useCases/useCaseCompleteJob';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import useSnackbar from 'targets/web/modules/dashboard/hooks/useSnackbar';

import { StatusChip } from './StatusChip';
import { JobCloseoutModal } from './JobCloseoutModal';

export const JobStatusChip = ({
  id,
  status,
  billingStatus,
  due,
  optimisticPermissions,
}: Pick<JobSummary, 'id' | 'status' | 'due' | 'billingStatus'> & {
  optimisticPermissions?: boolean;
}) => {
  const t = useTranslationPrefix('components.cards');
  const notify = useSnackbar();

  const [isCloseoutModalOpen, setIsCloseoutModalOpen] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const { updateJob } = useCaseUpdateJob();
  const { completeJob } = useCaseCompleteJob();
  const editable = !isEntityLocked({ billingStatus, status });

  const { granted: editGranted } = usePermission(
    editable
      ? {
          resource: `${JobResourceType}:${id}`,
          scope: 'update-statuses',
        }
      : undefined,
    optimisticPermissions,
  );

  const handleCompleteJob = useCallback(
    async (data: {
      securitySealNumber?: string;
      closeoutNotes?: string;
      attachments: AttachmentFile[];
    }) => {
      setIsCompleting(true);

      completeJob({
        jobId: id,
        securitySealNumber: data.securitySealNumber,
        closeoutNotes: data.closeoutNotes,
        attachments: data.attachments.map((a) => ({
          name: a.name,
          contentType: a.contentType,
          uri: a.uri,
        })),
      })
        .then(() => {
          notify(t('complete_success'), {
            variant: 'success',
          });
          setIsCloseoutModalOpen(false);
        })
        .catch((error) => {
          const errorCode = error.body?.message || error.message || 'unknown';
          const message = ['worklog_not_finished', 'services_unfinished'].includes(errorCode)
            ? t(`complete_errors.${errorCode}`)
            : errorCode;
          notify(t('complete_error', { error: message }), {
            variant: 'error',
          });
        })
        .finally(() => {
          setIsCompleting(false);
        });
    },
    [id, notify, completeJob, t],
  );

  const handleStatusChipChange = useCallback(
    async (newStatus: 'submitted' | 'in_progress' | 'completed') => {
      // Only show modal if transitioning TO completed (not already completed)
      if (newStatus === 'completed') {
        if (status !== 'completed') {
          setIsCloseoutModalOpen(true);
        }
        return;
      }

      await updateJob(
        {
          jobId: id,
          data: {
            status: newStatus === 'submitted' ? 'scheduled' : newStatus,
          },
        },
        {
          onError: () => {
            notify("Error occurred while updating the Job's status", {
              variant: 'error',
            });
          },
        },
      );
    },
    [id, notify, updateJob, status],
  );

  return (
    <>
      <StatusChip
        status={status}
        dueDate={due}
        editable={editable && editGranted}
        availableStatusChanges={['scheduled', 'in_progress', 'completed']}
        onSelect={handleStatusChipChange}
      />

      {isCloseoutModalOpen && (
        <JobCloseoutModal
          open={isCloseoutModalOpen}
          onClose={() => setIsCloseoutModalOpen(false)}
          onComplete={handleCompleteJob}
          loading={isCompleting}
        />
      )}
    </>
  );
};
